import { type FC } from 'react'

import { trfSignatureDisplay, type OrderTrfSignature } from '@/utils/orderTrfSignature'

/**
 * Where an order's TRF stands in the e-signature flow (GEN-4030).
 *
 * Renders nothing when the order has no outstanding signature request, which is the
 * normal state for organizations without e-signature enabled and for orders that
 * predate it. Showing "not signed" there would imply a signature is outstanding
 * when none was ever requested.
 *
 * The wording depends on the signing channel as well as the status, so that an
 * embedded request — which is never mailed to anyone — is not described in the
 * emailed flow's words. See `trfSignatureDisplay`.
 *
 * A declined envelope also offers a control to read the reason the signer typed.
 * That reason is optional on DocuSign's side, so the control still renders when
 * none was given — with copy that says so, rather than an empty panel.
 *
 * The reason itself is rendered by the caller, as `TrfDeclineReasonRow`, not from
 * inside the badge. The badge sits in a narrow column of a vertically centred
 * header row, so a panel growing inside it would widen that column and re-centre
 * everything beside it. Expanding must move nothing but the page below.
 */

interface TrfSignatureBadgeProps {
  signature: OrderTrfSignature | null
  className?: string
  /**
   * Whether the caller is currently showing the reason. Drives the control's
   * wording and `aria-expanded`.
   */
  declineReasonOpen?: boolean
  /**
   * Opens and closes the reason row. Omit it and a declined badge renders without
   * a control, since there is then no panel anywhere to reveal.
   */
  onToggleDeclineReason?: () => void
}

const TONE_CLASSES: Record<ReturnType<typeof trfSignatureDisplay>['tone'], string> = {
  pending: 'border-warning-300 dark:border-warning-600 bg-warning-50 dark:bg-gray-900 text-text-fg-warning dark:text-warning-100',
  success: 'border-border-success-subtle dark:border-success-700 bg-success-50 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100',
  warning: 'border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 text-text-fg-error-strong dark:text-error-100',
}

function formatUpdatedAt(updatedAt: Date | null): string | null {
  if (!updatedAt) return null
  return `${updatedAt.toLocaleDateString('en-US')} at ${updatedAt.toLocaleTimeString('en-US')}`
}

const NO_DECLINE_REASON = 'The signer did not provide a reason.'

/** Ties the badge's control to the row the caller renders, across the DOM. */
const DECLINE_REASON_ID = 'trf-signature-decline-reason'

/**
 * The signer's decline reason, as its own full-width row under the header row.
 *
 * Kept out of the badge's column on purpose: as a sibling of that row it can grow
 * without widening the patient details beside it or re-centring the action buttons.
 */
export const TrfDeclineReasonRow: FC<{ reason: string | null; className?: string }> = ({
  reason,
  className = '',
}) => (
  <div className={`mt-3 border-t border-border-base dark:border-gray-700 pt-2 ${className}`.trim()}>
    <p id={DECLINE_REASON_ID} data-testid="trf-signature-decline-reason" className="text-xs text-text-fg-error-strong/80 dark:text-error-100/80">
      {reason ?? NO_DECLINE_REASON}
    </p>
  </div>
)

export const TrfSignatureBadge: FC<TrfSignatureBadgeProps> = ({
  signature,
  className = '',
  declineReasonOpen = false,
  onToggleDeclineReason,
}) => {
  if (!signature) return null

  const { label, tone } = trfSignatureDisplay(signature.status, signature.channel)
  const updatedAt = formatUpdatedAt(signature.updatedAt)

  return (
    <div data-testid="trf-signature-status" className={`flex flex-col items-start gap-1 ${className}`.trim()}>
      <div
        data-testid="trf-signature-badge"
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${TONE_CLASSES[tone]}`}
      >
        <span className="text-sm font-medium">{label}</span>
        {updatedAt && <span className="text-xs opacity-75">{updatedAt}</span>}
      </div>
      {signature.status === 'declined' && onToggleDeclineReason ? (
        <button
          type="button"
          data-testid="trf-signature-decline-reason-toggle"
          aria-expanded={declineReasonOpen}
          aria-controls={DECLINE_REASON_ID}
          className="pl-1 text-xs font-medium text-text-fg-error-strong dark:text-error-100 underline-offset-2 hover:underline"
          onClick={onToggleDeclineReason}
        >
          {declineReasonOpen ? 'Hide reason' : 'View reason'}
        </button>
      ) : null}
    </div>
  )
}

export default TrfSignatureBadge
